import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Star, ArrowLeft, ExternalLink, Copy, Check, 
  Chrome, Facebook, MapPin, Phone, User, MessageSquare, HelpCircle,
  Home, BedDouble, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  chrome: Chrome,
  facebook: Facebook,
  'map-pin': MapPin,
  star: Star,
};

const PlaqueDetailPage = () => {
  const { id } = useParams();
  const { user, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [plaque, setPlaque] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    reviewUrl: '',
    thresholdStars: '4',
    businessName: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [plaqueRes, ratingsRes] = await Promise.all([
        axios.get(`${API}/plaques`),
        axios.get(`${API}/plaques/${id}/ratings`)
      ]);
      
      const foundPlaque = plaqueRes.data.find(p => p.id === id);
      if (!foundPlaque) {
        toast.error('Plaque not found');
        navigate('/dashboard');
        return;
      }
      
      setPlaque(foundPlaque);
      setRatings(ratingsRes.data);
      setFormData({
        reviewUrl: foundPlaque.review_url || '',
        thresholdStars: String(foundPlaque.threshold_stars || 4),
        businessName: foundPlaque.business_name || ''
      });
    } catch (error) {
      toast.error('Failed to load plaque details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/plaques/${id}`, {
        review_url: formData.reviewUrl,
        threshold_stars: parseInt(formData.thresholdStars),
        business_name: formData.businessName
      });
      
      toast.success('Plaque updated successfully');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plaque');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const publicUrl = `${window.location.origin}/r/${plaque?.code}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!plaque) return null;

  const PlatformIcon = platformIcons[plaque.platform_icon] || Star;

  // Calculate stats
  const totalRatings = ratings.length;
  const avgRating = totalRatings > 0 
    ? (ratings.reduce((sum, r) => sum + r.stars, 0) / totalRatings).toFixed(1)
    : 0;
  const positiveRatings = ratings.filter(r => r.stars >= plaque.threshold_stars).length;
  const negativeRatings = ratings.filter(r => r.stars < plaque.threshold_stars).length;

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>

        {/* Header Card */}
        <Card className="shadow-layered border-0 mb-6">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <PlatformIcon className="w-6 h-6 text-[var(--color-accent)]" />
                </div>
                <div>
                  <CardTitle className="font-dashboard-heading text-xl font-bold flex items-center gap-2">
                    {plaque.code}
                    <Badge className={plaque.is_registered ? 'badge-active' : 'badge-inactive'}>
                      {plaque.is_registered ? 'Active' : 'Pending'}
                    </Badge>
                  </CardTitle>
                  <CardDescription>{plaque.platform_name}</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {!editMode ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setEditMode(true)}
                    data-testid="edit-plaque-btn"
                  >
                    Edit Settings
                  </Button>
                ) : (
                  <>
                    <Button variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="save-plaque-btn">
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Public URL */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <Label className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">
                Public Rating URL
              </Label>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono overflow-x-auto">
                  {publicUrl}
                </code>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => copyToClipboard(publicUrl)}
                  data-testid="copy-url-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Edit Form or Display */}
            {editMode ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="businessName">Business Name</Label>
                  <Input
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    data-testid="edit-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reviewUrl">Review Page URL</Label>
                  <Input
                    id="reviewUrl"
                    value={formData.reviewUrl}
                    onChange={(e) => setFormData({ ...formData, reviewUrl: e.target.value })}
                    data-testid="edit-review-url"
                  />
                  <a 
                    href="https://www.reviewmybusiness.ie/help/reivewurl" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline mt-1"
                    data-testid="edit-review-url-help-link"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Don't know what to put here? Click here for help.
                  </a>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threshold">Minimum Stars for Public Review</Label>
                  <Select
                    value={formData.thresholdStars}
                    onValueChange={(value) => setFormData({ ...formData, thresholdStars: value })}
                  >
                    <SelectTrigger data-testid="edit-threshold-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 stars or more</SelectItem>
                      <SelectItem value="4">4 stars or more</SelectItem>
                      <SelectItem value="5">5 stars only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">Business</p>
                  <p className="font-medium mt-1">{plaque.business_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">Threshold</p>
                  <p className="font-medium mt-1 flex items-center gap-1">
                    {plaque.threshold_stars}
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">Created</p>
                  <p className="font-medium mt-1">
                    {new Date(plaque.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">Registered</p>
                  <p className="font-medium mt-1">
                    {plaque.registered_at ? new Date(plaque.registered_at).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold">{totalRatings}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Total Ratings</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold">{avgRating}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Avg Rating</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-green-600">{positiveRatings}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">To Reviews</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-dashboard-heading font-bold text-amber-600">{negativeRatings}</p>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide">Feedback</p>
            </CardContent>
          </Card>
        </div>

        {/* Ratings List */}
        <Card className="shadow-layered border-0">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="font-dashboard-heading text-lg font-semibold">
              Customer Ratings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {ratings.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-dashboard-heading font-semibold text-lg mb-2">No ratings yet</h3>
                <p className="text-[var(--color-text-muted)]">
                  Ratings will appear here when customers use your plaque
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {ratings.map((rating) => (
                  <div key={rating.id} className="p-4 hover:bg-gray-50" data-testid={`rating-${rating.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= rating.stars 
                                    ? 'fill-yellow-400 text-yellow-400' 
                                    : 'text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                          <Badge className={rating.stars >= plaque.threshold_stars ? 'badge-active' : 'badge-inactive'}>
                            {rating.stars >= plaque.threshold_stars ? 'To Review' : 'Feedback'}
                          </Badge>
                        </div>
                        {rating.feedback && (
                          <p className="text-[var(--color-text-secondary)] mb-2">{rating.feedback}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-[var(--color-text-muted)]">
                          {rating.customer_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {rating.customer_name}
                            </span>
                          )}
                          {rating.customer_mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {rating.customer_mobile}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlaqueDetailPage;
